// Asignacion rapida por codigo de movil (PP-65). El proveedor ya despacho el
// viaje en su sistema propio, que nombra cada movil por su numero interno: ese
// numero es el codigo de la nomina (ADR-014, unico en la nomina de la empresa
// madre e igual en todas las plantas). Escribirlo selecciona el vehiculo y
// propone un conductor.
//
// "Conductor habitual" no existe en el modelo. La propuesta es el ultimo
// conductor asignado a ese vehiculo en los viajes de la empresa en esta planta
// que la bandeja ya tiene cargados: sin columna, sin lectura nueva y sin
// cruzar plantas (la bandeja es de una habilitacion). Es solo una propuesta:
// la persona la ve y la cambia, y la API valida igual la asignacion.
//
// La busqueda es solo en la nomina de la empresa de la sesion: un codigo de
// otra empresa da el mismo mensaje que uno inexistente, sin confirmar que
// exista en otra nomina.
import type { Driver, ProviderTrip, SiteVehicle } from "@ucorp/contracts";

type RosterRow = Pick<SiteVehicle, "id" | "code" | "providerId" | "inRoster">;

export type CodeLookup<T> =
  | { readonly kind: "empty" }
  /** Encontrado y en la nomina; puede no ser elegible (el formulario lo advierte). */
  | { readonly kind: "found"; readonly entry: T }
  /** Dado de baja de la nomina: no se asigna (ADR-014). */
  | { readonly kind: "removed"; readonly entry: T }
  /** Ni exacto ni con la escritura flexible: inexistente o de otra empresa. */
  | { readonly kind: "unknown" }
  /** La escritura flexible coincide con mas de uno: hay que escribirlo exacto. */
  | { readonly kind: "ambiguous"; readonly codes: readonly string[] };

/** Mayusculas y sin espacios: "m 14" y "M14" son lo mismo para una persona. */
function exactKey(code: string): string {
  return code.replace(/\s+/g, "").toUpperCase();
}

/** Ademas sin guiones, puntos ni barras: "m14" encuentra "M-14". */
function looseKey(code: string): string {
  return exactKey(code).replace(/[-_./]/g, "");
}

/**
 * Busca un codigo de movil en la nomina de `providerId`. Primero exacto (sin
 * importar mayusculas ni espacios); si no, ignorando separadores. Una entrada
 * de otra empresa nunca coincide, aunque venga en la lista.
 */
export function findByRosterCode<T extends RosterRow>(rows: readonly T[], providerId: string, typed: string): CodeLookup<T> {
  const key = exactKey(typed);
  if (key === "") return { kind: "empty" };
  const own = rows.filter((row) => row.providerId === providerId);
  const pick = (matches: readonly T[]): CodeLookup<T> | undefined => {
    // Una baja y un activo con el mismo codigo no deberian convivir; si pasa, gana el de la nomina.
    const live = matches.filter((row) => row.inRoster);
    if (live.length === 1) return { kind: "found", entry: live[0]! };
    if (live.length > 1) return { kind: "ambiguous", codes: live.map((row) => row.code).sort() };
    if (matches.length > 0) return { kind: "removed", entry: matches[0]! };
    return undefined;
  };
  const exact = pick(own.filter((row) => exactKey(row.code) === key));
  if (exact !== undefined) return exact;
  const loose = looseKey(typed);
  if (loose === "") return { kind: "unknown" };
  return pick(own.filter((row) => looseKey(row.code) === loose)) ?? { kind: "unknown" };
}

type AssignedTrip = Pick<ProviderTrip, "tripId" | "providerId" | "date" | "assignmentStatus" | "vehicleId" | "driverId" | "summary">;

/**
 * Ultimo conductor asignado a cada vehiculo, de los viajes de la empresa.
 * "Ultimo" por fecha operativa y hora de salida; el viaje que se esta
 * asignando no cuenta (si no, propondria lo que ya tiene).
 */
export function lastDriverByVehicle(trips: readonly AssignedTrip[], providerId: string, excludeTripId?: string): Map<string, string> {
  const assigned = trips.filter(
    (trip) =>
      trip.providerId === providerId &&
      trip.tripId !== excludeTripId &&
      trip.assignmentStatus === "assigned" &&
      trip.vehicleId !== undefined &&
      trip.driverId !== undefined,
  );
  const when = (trip: AssignedTrip): string => `${trip.date}T${trip.summary?.departureAt ?? ""}`;
  assigned.sort((a, b) => (when(a) < when(b) ? -1 : when(a) > when(b) ? 1 : a.tripId < b.tripId ? -1 : 1));
  const last = new Map<string, string>();
  for (const trip of assigned) last.set(trip.vehicleId!, trip.driverId!);
  return last;
}

/**
 * Conductor propuesto para un vehiculo: el ultimo que lo manejo, si sigue en
 * la nomina de la empresa. Puede no ser elegible: se propone igual y el
 * formulario lo advierte, como cualquier eleccion.
 */
export function proposeDriver<D extends Pick<Driver, "id" | "providerId" | "inRoster">>(
  vehicleId: string,
  lastDrivers: ReadonlyMap<string, string>,
  drivers: readonly D[],
  providerId: string,
): D | undefined {
  const driverId = lastDrivers.get(vehicleId);
  if (driverId === undefined) return undefined;
  return drivers.find((d) => d.id === driverId && d.providerId === providerId && d.inRoster);
}
